import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { hasInAppHistory } from '../hooks/useSmartBack';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Lock, Check, Coins, FileText, Sparkles, ChevronRight } from 'lucide-react';
import { CategoryHeader } from '../components/student-hub';
import { getApiBase } from '../api/httpClient';
import { safeReadResponse, pickErrorMessage } from '../api/safeFetch';
import { COINS_DISABLED } from '../config/coinsFlag';
import '../components/student-hub/CategoryComponents.css';
import './StudentHub.css';
import './StudentCVPage.css';

// CV Tiers
const CV_TIERS = {
  free: {
    id: 'free',
    name_en: 'Free',
    name_ar: 'مجاني',
    price: 0,
    features_en: ['1 Basic Template', 'PDF Export (with watermark)'],
    features_ar: ['قالب أساسي واحد', 'تصدير PDF (مع علامة مائية)'],
  },
  pro: {
    id: 'pro',
    name_en: 'Pro',
    name_ar: 'متقدم',
    price: 10,
    unlock_id: 'premium_templates',
    features_en: ['5+ Premium Templates', 'No Watermark', 'Cover Letter Builder', 'ATS Optimization Tips'],
    features_ar: ['5+ قوالب متقدمة', 'بدون علامة مائية', 'منشئ خطاب التغطية', 'نصائح تحسين ATS'],
  },
};

// CV Templates
const CV_TEMPLATES = [
  { id: 'basic', tier: 'free', name_en: 'Basic', name_ar: 'أساسي', preview: '/templates/basic.svg' },
  { id: 'modern', tier: 'pro', name_en: 'Modern', name_ar: 'عصري', preview: '/templates/modern.svg' },
  { id: 'executive', tier: 'pro', name_en: 'Executive', name_ar: 'تنفيذي', preview: '/templates/executive.svg' },
  { id: 'creative', tier: 'pro', name_en: 'Creative', name_ar: 'إبداعي', preview: '/templates/creative.svg' },
  { id: 'minimal', tier: 'pro', name_en: 'Minimal', name_ar: 'بسيط', preview: '/templates/minimal.svg' },
  { id: 'professional', tier: 'pro', name_en: 'Professional', name_ar: 'احترافي', preview: '/templates/professional.svg' },
];

// Unlock Items
const UNLOCK_ITEMS = [
  {
    id: 'premium_templates',
    item_type: 'template_pack',
    name_en: 'Premium Templates Pack',
    name_ar: 'حزمة القوالب المميزة',
    description_en: 'Unlock 5+ premium CV templates',
    description_ar: 'فتح 5+ قوالب سيرة ذاتية مميزة',
    price_coins: 10,
    icon: '📄',
  },
  {
    id: 'cover_letter_builder',
    item_type: 'cover_letter_pack',
    name_en: 'Cover Letter Builder',
    name_ar: 'منشئ خطاب التغطية',
    description_en: 'Create professional cover letters',
    description_ar: 'إنشاء خطابات تغطية احترافية',
    price_coins: 5,
    icon: '✉️',
  },
  {
    id: 'ats_optimization',
    item_type: 'ats_tips',
    name_en: 'ATS Optimization Guide',
    name_ar: 'دليل تحسين ATS',
    description_en: 'Beat applicant tracking systems',
    description_ar: 'تجاوز أنظمة تتبع المتقدمين',
    price_coins: 3,
    icon: '🎯',
  },
];

export default function StudentCVPage() {
  const { t, i18n } = useTranslation();
  // iter304 — the CV Builder MUST read coins from the same source the
  // header wallet pill + the Wallet page read from. AuthContext is that
  // canonical source (it owns `user.coins`, hydrated from /api/auth/me).
  // The old code fetched /api/auth/profile which 405's, silently caught,
  // and left a stale `0` showing — blocking purchases for users who
  // actually had enough coins on wassla.tech.
  const { user, token, loading: authLoading, updateCoins } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const isArabic = i18n.language?.startsWith('ar');
  const isRTL = document.dir === 'rtl' || document.documentElement.dir === 'rtl' || isArabic;
  const lang = isRTL ? 'ar' : 'en';
  
  // State
  const [unlocks, setUnlocks] = useState([]);
  // Server catalog prices (single source of truth) — id -> price_coins
  const [catalog, setCatalog] = useState(null);
  const [unlocksLoading, setUnlocksLoading] = useState(true);
  const [unlockModalItem, setUnlockModalItem] = useState(null);
  const [processing, setProcessing] = useState(false);
  // iter304 — guard against double-charge from rapid taps (the boost
  // flow already does this via Idempotency-Key; CV Builder now does too).
  const [unlockIdempotencyKey, setUnlockIdempotencyKey] = useState(null);

  // Canonical wallet balance — single source of truth.
  // `null` means "we don't know yet" (auth still hydrating); `number` is
  // the real, current balance. NEVER show "0" while we don't know.
  const userCoins = (user && typeof user.coins === 'number') ? user.coins : null;
  const walletKnown = userCoins !== null;
  const walletLoading = authLoading || !walletKnown;

  // Play compliance build: only coin-free templates are shown or reachable.
  const visibleTemplates = COINS_DISABLED
    ? CV_TEMPLATES.filter(tp => tp.tier === 'free')
    : CV_TEMPLATES;

  // Fetch unlocks on mount. Coins now come from AuthContext — no fetch needed.
  useEffect(() => {
    let cancelled = false;
    async function loadUnlocks() {
      if (!user || !token || COINS_DISABLED) {
        if (!cancelled) setUnlocksLoading(false);
        return;
      }
      try {
        const res = await fetch(`${getApiBase()}/api/cv/unlocks`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const parsed = await safeReadResponse(res);
        if (!cancelled && parsed.ok) {
          setUnlocks(parsed.data?.unlocks || []);
          const items = parsed.data?.available_items;
          if (Array.isArray(items)) {
            setCatalog(Object.fromEntries(items.map(i => [i.id, i.price_coins])));
          }
        }
      } catch (e) {
        console.error('[StudentCVPage] fetchUnlocks failed:', e);
      } finally {
        if (!cancelled) setUnlocksLoading(false);
      }
    }
    loadUnlocks();
    return () => { cancelled = true; };
  }, [user, token]);

  // Live-refresh the canonical balance whenever this spend surface opens.
  // Server-side awards (streaks, referrals, milestones) change coins without
  // the auth context knowing, which used to cause false "Not enough coins".
  useEffect(() => {
    if (!user || !token || COINS_DISABLED) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${getApiBase()}/api/wallet`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled && typeof d.balance === 'number') updateCoins(d.balance);
      } catch (_e) { /* keep context value */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id, token]);

  const isUnlocked = (itemId) => {
    return unlocks.some(u => u.item_id === itemId);
  };
  
  const hasProAccess = isUnlocked('premium_templates');
  // Display price always comes from the server catalog when available.
  const priceOf = (item) => (catalog && typeof catalog[item.id] === 'number') ? catalog[item.id] : item.price_coins;
  const proItem = UNLOCK_ITEMS[0];
  
  const handleUnlock = useCallback(async (item) => {
    if (!user || !token) {
      navigate('/welcome');
      return;
    }

    // iter304 — never let a "still loading" balance be interpreted as
    // "not enough coins". If the wallet hasn't hydrated yet, just wait.
    if (!walletKnown) {
      return;
    }

    // iter382 — no hard client-side affordability block: the server's
    // atomic `balance >= cost` check is the single source of truth, so a
    // stale cached balance can never falsely reject a valid purchase.
    // A genuinely-insufficient balance gets the server's 402 below.

    setProcessing(true);

    // Reuse the same idempotency key across rapid double-taps so the
    // backend can dedupe — pattern matches iter303 boost behaviour.
    const idemKey = unlockIdempotencyKey || (() => {
      const k = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `cvunlock_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      setUnlockIdempotencyKey(k);
      return k;
    })();

    try {
      const res = await fetch(`${getApiBase()}/api/cv/unlock`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Idempotency-Key': idemKey,
        },
        body: JSON.stringify({
          item_type: item.item_type,
          item_id: item.id,
          idempotency_key: idemKey,
        })
      });

      const parsed = await safeReadResponse(res);

      if (parsed.ok) {
        const data = parsed.data || {};

        // Update local entitlements
        setUnlocks(prev => {
          // Don't duplicate if already present (replay path).
          if (prev.some(u => u.item_id === item.id)) return prev;
          return [...prev, { item_id: item.id, item_type: item.item_type }];
        });

        // CANONICAL update — propagates the new balance to the header
        // wallet pill, the Wallet page, every other open modal, etc.
        if (typeof data.coins_remaining === 'number') {
          updateCoins(data.coins_remaining);
        }

        setUnlockModalItem(null);
        setUnlockIdempotencyKey(null); // fresh key for next purchase
        toast.success(t('studpage.itemUnlockedSuccessfully'));
      } else {
        const detail = parsed.data?.detail;
        const errKey = typeof detail === 'object' ? detail?.error : null;
        if (errKey === 'insufficient_coins') {
          toast.error(t('studpage.notEnoughCoins'));
          navigate('/wallet', { state: { from: location.pathname } });
        } else {
          toast.error(pickErrorMessage(parsed, t('studpage.somethingWentWrong')));
        }
      }
    } catch (e) {
      console.error('[StudentCVPage] unlock failed:', e);
      toast.error(t('studpage.somethingWentWrong'));
    }

    setProcessing(false);
  }, [user, token, userCoins, walletKnown, unlockIdempotencyKey, t, navigate, location.pathname, updateCoins]);
  
  const handleTemplateClick = (template) => {
    if (template.tier === 'free' || hasProAccess) {
      // Navigate to CV builder with template
      navigate('/student-hub/cv-builder', { 
        state: { 
          template: template.id,
          from: location.pathname 
        } 
      });
    } else if (!COINS_DISABLED) {
      // Show unlock modal
      setUnlockModalItem(UNLOCK_ITEMS.find(i => i.id === 'premium_templates'));
    }
  };
  
  const handleBack = () => {
    if (hasInAppHistory()) return navigate(-1);
    navigate(location.state?.from || '/student-hub', { replace: true });
  };
  
  return (
    <div className="student-cv-page" dir={isRTL ? 'rtl' : 'ltr'} data-testid="student-cv-page">
      {/* Reusable Header */}
      <div className="cv-header-wrapper">
        <CategoryHeader
          icon="📝"
          title={{ en: 'CV & Cover Letter', ar: 'السيرة الذاتية' }}
          subtitle={{ en: 'Build your professional CV', ar: 'أنشئ سيرتك الذاتية الاحترافية' }}
          onBack={handleBack}
          showLevel={false}
        />
        
      </div>
      
      {/* Tiers Section — Play compliance build shows a single coin-free start card */}
      {COINS_DISABLED ? (
        <div className="cv-tiers-section">
          <div className="tier-card tier-free" data-testid="tier-free">
            <div className="tier-header">
              <span className="tier-name">{isRTL ? 'السيرة الذاتية' : 'CV Builder'}</span>
            </div>
            <ul className="tier-features">
              <li><Check size={14} />{isRTL ? 'قوالب سيرة ذاتية' : 'CV templates'}</li>
              <li><Check size={14} />{isRTL ? 'تصدير PDF' : 'PDF Export'}</li>
            </ul>
            <Button
              variant="outline"
              className="tier-btn"
              onClick={() => navigate('/student-hub/cv-builder', { state: { plan: 'free', from: location.pathname } })}
              data-testid="tier-free-cta"
            >
              {isRTL ? 'ابدأ الآن' : 'Start building'}
            </Button>
          </div>
        </div>
      ) : (
      <div className="cv-tiers-section">
        <h2 className="section-title">{t('studpage.chooseYourPlan')}</h2>
        
        <div className="tiers-grid">
          {/* Free Tier — compact, clean */}
          <div className="tier-card tier-free" data-testid="tier-free">
            <div className="tier-header">
              <span className="tier-name">{isRTL ? 'مجاني' : 'Free'}</span>
            </div>
            <ul className="tier-features">
              {(isRTL ? CV_TIERS.free.features_ar : CV_TIERS.free.features_en).map((f, i) => (
                <li key={i}>
                  <Check size={14} />
                  {f}
                </li>
              ))}
            </ul>
            <Button 
              variant="outline" 
              className="tier-btn"
              onClick={() => navigate('/student-hub/cv-builder', { state: { plan: 'free', from: location.pathname } })}
              data-testid="tier-free-cta"
            >
              {isRTL ? 'ابدأ مجاناً' : 'Start with Free'}
            </Button>
          </div>
          
          {/* Pro Tier — highlighted, conversion-focused */}
          <div className={`tier-card tier-pro ${hasProAccess ? 'unlocked' : ''}`} data-testid="tier-pro">
            {!hasProAccess && (
              <div className="tier-badge">
                <Sparkles size={12} />
                {isRTL ? 'الأكثر اختياراً' : 'Most chosen'}
              </div>
            )}
            <div className="tier-header">
              <span className="tier-name">{isRTL ? 'احترافي' : 'Pro'}</span>
              <span className="tier-price">
                {hasProAccess ? (
                  <span className="unlocked-badge">
                    <Check size={14} />
                    {t('studpage.unlocked')}
                  </span>
                ) : (
                  <>
                    <Coins size={14} />
                    {priceOf(proItem)} {isRTL ? 'عملات' : 'coins'}
                  </>
                )}
              </span>
            </div>
            <ul className="tier-features">
              {(isRTL ? CV_TIERS.pro.features_ar : CV_TIERS.pro.features_en).map((f, i) => (
                <li key={i}>
                  <Check size={14} />
                  {f}
                </li>
              ))}
            </ul>
            {!hasProAccess ? (
              <Button 
                className="tier-btn tier-btn-pro"
                onClick={() => setUnlockModalItem(UNLOCK_ITEMS.find(i => i.id === 'premium_templates'))}
                data-testid="tier-pro-cta"
              >
                <Lock size={16} />
                {isRTL ? `فتح Pro (${priceOf(proItem)} عملات)` : `Unlock Pro (${priceOf(proItem)} coins)`}
              </Button>
            ) : (
              <Button 
                className="tier-btn"
                onClick={() => navigate('/student-hub/cv-builder', { state: { plan: 'pro', from: location.pathname } })}
              >
                {t('studpage.useTemplates')}
              </Button>
            )}
          </div>
        </div>
        
        {/* Helper text */}
        <p className="cv-plan-helper" data-testid="cv-plan-helper">
          {isRTL ? 'اختر قالبًا لبدء إنشاء سيرتك الذاتية. يمكنك الترقية في أي وقت.' : 'Choose a template to start building your CV. You can upgrade anytime.'}
        </p>
      </div>
      )}
      
      {/* Templates Section */}
      <div className="cv-templates-section">
        <h2 className="section-title">{t('studpage.availableTemplates')}</h2>
        
        <div className="templates-grid">
          {visibleTemplates.map(template => {
            const canAccess = template.tier === 'free' || hasProAccess;
            
            return (
              <div 
                key={template.id}
                className={`template-card ${!canAccess ? 'locked' : ''}`}
                onClick={() => handleTemplateClick(template)}
                data-testid={`template-${template.id}`}
              >
                <div className="template-preview">
                  <div className={`template-mini-cv mini-cv-${template.id}`}>
                    <div className="mini-header" />
                    <div className="mini-lines">
                      <div className="mini-line w80" />
                      <div className="mini-line w60" />
                    </div>
                    <div className="mini-section">
                      <div className="mini-line w40 bold" />
                      <div className="mini-line w90" />
                      <div className="mini-line w70" />
                    </div>
                    <div className="mini-section">
                      <div className="mini-line w40 bold" />
                      <div className="mini-dots" />
                    </div>
                  </div>
                  {!canAccess && (
                    <div className="template-lock">
                      <Lock size={20} />
                    </div>
                  )}
                </div>
                <div className="template-info">
                  <span className="template-name">
                    {isRTL ? template.name_ar : template.name_en}
                  </span>
                  <Badge variant={canAccess ? 'outline' : 'secondary'}>
                    {canAccess ? (t('studpage.available')) : (t('studpage.locked'))}
                  </Badge>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Additional Items Section — coin-priced tools, hidden on the Play build */}
      {!COINS_DISABLED && (
      <div className="cv-extras-section">
        <h2 className="section-title">{t('studpage.additionalTools')}</h2>
        
        <div className="extras-list">
          {UNLOCK_ITEMS.slice(1).map(item => {
            const unlocked = isUnlocked(item.id);
            
            return (
              <div 
                key={item.id}
                className={`extra-card ${unlocked ? 'unlocked' : ''}`}
                onClick={() => !unlocked && setUnlockModalItem(item)}
              >
                <span className="extra-icon">{item.icon}</span>
                <div className="extra-content">
                  <span className="extra-name">
                    {isRTL ? item.name_ar : item.name_en}
                  </span>
                  <span className="extra-desc">
                    {isRTL ? item.description_ar : item.description_en}
                  </span>
                </div>
                <div className="extra-action">
                  {unlocked ? (
                    <Badge variant="outline" className="unlocked-badge">
                      <Check size={12} />
                      {t('studpage.unlocked')}
                    </Badge>
                  ) : (
                    <Badge className="price-badge">
                      <Coins size={12} />
                      {priceOf(item)}
                    </Badge>
                  )}
                  <ChevronRight size={16} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}
      
      {/* Unlock Modal — portaled to <body> so no ancestor stacking context
          can trap it under the fixed bottom nav / FAB */}
      {!COINS_DISABLED && unlockModalItem && createPortal(
        <div className="unlock-modal-overlay" onClick={() => setUnlockModalItem(null)}>
          <div className="unlock-modal" onClick={(e) => e.stopPropagation()}>
            <div className="unlock-modal-header">
              <span className="unlock-icon">{unlockModalItem.icon || '🔓'}</span>
              <h3>{isRTL ? unlockModalItem.name_ar : unlockModalItem.name_en}</h3>
              <p>{isRTL ? unlockModalItem.description_ar : unlockModalItem.description_en}</p>
            </div>
            
            <div className="unlock-modal-benefits">
              <h4>{t('studpage.benefits')}</h4>
              {unlockModalItem.id === 'premium_templates' && (
                <ul>
                  {(isRTL ? CV_TIERS.pro.features_ar : CV_TIERS.pro.features_en).map((f, i) => (
                    <li key={i}><Check size={14} />{f}</li>
                  ))}
                </ul>
              )}
            </div>
            
            <div className="unlock-modal-price">
              <span className="price-label">{t('studpage.price')}</span>
              <span className="price-value">
                <Coins size={18} />
                {priceOf(unlockModalItem)} {t('coins')}
              </span>
              <span className="balance-label" data-testid="cv-modal-balance">
                {t('studpage.yourBalance')}{' '}
                {walletLoading
                  ? (isRTL ? '…' : '…')
                  : `${userCoins} ${t('coins')}`}
              </span>
            </div>
            
            <div className="unlock-modal-actions">
              <Button 
                variant="outline" 
                onClick={() => setUnlockModalItem(null)}
              >
                {t('bam.cancel')}
              </Button>
              
              {walletLoading ? (
                <Button
                  className="unlock-btn"
                  disabled
                  data-testid="cv-modal-loading-btn"
                >
                  {t('studpage.unlocking') /* shows a spinner-like state */}
                </Button>
              ) : userCoins >= priceOf(unlockModalItem) ? (
                <Button 
                  className="unlock-btn"
                  onClick={() => handleUnlock(unlockModalItem)}
                  disabled={processing}
                  data-testid="cv-modal-unlock-btn"
                >
                  {processing ? (
                    t('studpage.unlocking')
                  ) : (
                    <>
                      <Lock size={16} />
                      {isRTL
                        ? `فتح (${priceOf(unlockModalItem)} عملات)`
                        : `Unlock for ${priceOf(unlockModalItem)} coins`}
                    </>
                  )}
                </Button>
              ) : (
                <Button 
                  className="get-coins-btn"
                  onClick={() => navigate('/wallet', { state: { from: location.pathname } })}
                  data-testid="cv-modal-get-coins-btn"
                >
                  <Coins size={16} />
                  {t('studpage.getCoins')}
                </Button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
